import { IMediaFile } from "@/shared/types";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

interface IState {
  prompt: string;

  // ==== SETTINGS ====
  type: string;
  layout: string;
  aspectRatio: string;
  style: string;
  view: string;
  background: string;
  variant: string;
  // ==== SETTINGS ====

  referenceImages: {
    name: string;
    url: string;
    extension: string;
    loading: boolean;
    id: string;
  }[];
  colors: string[];
  colorsMode: "auto" | "manual";
  model: "basic" | "pro";
  showSuggestions: boolean;

  mediaFile: IMediaFile | null;
}

interface IActions {
  setPrompt: (prompt: string) => void;
  setType: (type: string) => void;
  setLayout: (layout: string) => void;
  setAspectRatio: (aspectRatio: string) => void;
  setVariant: (variant: string) => void;
  setStyle: (style: string) => void;
  setView: (view: string) => void;
  setBackground: (background: string) => void;
  setReferenceImages: (image: {
    name: string;
    url: string;
    extension: string;
    loading: boolean;
    id: string;
  }) => void;
  updateReferenceImage: (
    id: string,
    data: Partial<{
      name: string;
      url: string;
      extension: string;
      loading: boolean;
    }>,
  ) => void;
  removeReferenceImage: (id: string) => void;
  setColors: (colors: string[]) => void;
  setColorsMode: (colorsMode: "auto" | "manual") => void;
  setModel: (model: "basic" | "pro") => void;
  setMediaFile: (mediaFile: IMediaFile | null) => void;
  reset: () => void;
}

const initState: IState = {
  prompt: "",
  type: "image",
  layout: "",
  aspectRatio: "",
  style: "",
  view: "",
  background: "",
  referenceImages: [],
  colors: ["#FFFFFF", "#FFFFFF", "#FFFFFF"],
  colorsMode: "manual",
  model: "basic",
  mediaFile: null,
  variant: "",
  showSuggestions: false,
};

export const useAiImageStore = create<IState & IActions>((set, get) => ({
  ...initState,
  setPrompt: (prompt) => set({ prompt }),
  setType: (type) => {
    const payload = {
      type,
      layout: "",
      aspectRatio: "",
      style: "",
      view: "",
      background: "",
      variant: "",
      showSuggestions: true,
      model: get().model,
      colorsMode: get().colorsMode,
    };
    payload.colorsMode = "manual";
    payload.model = "basic";

    if (type === "diagram" || type === "infographic") {
      payload.model = "pro";
    }
    if (type === "icon") {
      payload.aspectRatio = "square";
    }
    set(payload);
  },
  setLayout: (layout) => set({ layout }),
  setVariant: (variant) => set({ variant }),
  setAspectRatio: (aspectRatio) => set({ aspectRatio }),
  setStyle: (style) => set({ style }),
  setView: (view) => set({ view }),
  setBackground: (background) => set({ background }),
  setColors: (colors) => set({ colors }),
  setColorsMode: (colorsMode) => set({ colorsMode }),
  setModel: (model) => set({ model }),
  setMediaFile: (mediaFile) => set({ mediaFile }),
  setReferenceImages: (image) => {
    set({ referenceImages: [...(get().referenceImages || []), image] });
  },
  removeReferenceImage: (id) => {
    set({
      referenceImages: get().referenceImages.filter((image) => image.id !== id),
    });
  },
  updateReferenceImage: (id, data) => {
    set({
      referenceImages: get().referenceImages.map((image) =>
        image.id === id ? { ...image, ...data } : image,
      ),
    });
  },
  reset: () => set(initState),
}));

export const useAiImageStoreShallow = <U>(
  selector: (store: IState & IActions) => U,
) => {
  return useAiImageStore(useShallow(selector));
};
